import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { EntityLinkSelector } from '@/components/shared/EntityLinkSelector';
import type { EntityType } from '@/types/link';

interface SelectedEntity {
  id: string;
  type: EntityType;
  name: string;
}

interface ControlCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: any, links?: SelectedEntity[]) => void;
  isCreating?: boolean;
}

export function ControlCreateDialog({ open, onOpenChange, onSave, isCreating }: ControlCreateDialogProps) {
  const [formData, setFormData] = useState({
    control_id: '',
    title: '',
    description: '',
    domain: '',
    status: 'not_implemented' as const,
    standard_key: 'ISO27001',
  });
  const [selectedLinks, setSelectedLinks] = useState<SelectedEntity[]>([]);

  const handleSubmit = () => {
    onSave(formData, selectedLinks);
    setFormData({
      control_id: '',
      title: '',
      description: '',
      domain: '',
      status: 'not_implemented',
      standard_key: 'ISO27001',
    });
    setSelectedLinks([]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Add New Control</DialogTitle>
          <DialogDescription>
            Create a new security or compliance control to track
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="control_id">Control ID *</Label>
              <Input
                id="control_id"
                value={formData.control_id}
                onChange={(e) => setFormData({ ...formData, control_id: e.target.value })}
                placeholder="e.g., AC-1"
              />
            </div>
            <div>
              <Label htmlFor="domain">Domain *</Label>
              <Select
                value={formData.domain}
                onValueChange={(value) => setFormData({ ...formData, domain: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select domain" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Access Control">Access Control</SelectItem>
                  <SelectItem value="Security">Security</SelectItem>
                  <SelectItem value="Privacy">Privacy</SelectItem>
                  <SelectItem value="Compliance">Compliance</SelectItem>
                  <SelectItem value="Operations">Operations</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div>
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Control title"
            />
          </div>
          
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe the control requirements"
              rows={4}
            />
          </div>
          
          <div>
            <Label htmlFor="status">Status</Label>
            <Select
              value={formData.status}
              onValueChange={(value) => setFormData({ ...formData, status: value as typeof formData.status })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="not_implemented">Not Implemented</SelectItem>
                <SelectItem value="partially_implemented">Partially Implemented</SelectItem>
                <SelectItem value="implemented">Implemented</SelectItem>
                <SelectItem value="not_applicable">Not Applicable</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <EntityLinkSelector
            allowedTypes={['asset', 'system', 'risk', 'evidence', 'supplier', 'incident']}
            onLinksChange={setSelectedLinks}
            initialLinks={selectedLinks}
          />
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={!formData.control_id || !formData.title || !formData.domain || !formData.standard_key || isCreating}
          >
            {isCreating ? 'Creating...' : 'Create Control'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
